import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ProductCard from '@/components/ProductCard';
import FreeShippingPromoBanner from '@/components/FreeShippingPromoBanner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { apiGetWithRetry } from '@/utils/api';
import { trackCategoryClick } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryTitles = {
  masculino: 'Moda Masculina',
  feminino: 'Moda Feminina',
  infantil: 'Moda Infantil',
};

export default function ProductsPage() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') || '';
  const subcategoryFilter = searchParams.get('subcategory') || '';
  const showNew = searchParams.get('new') === 'true';
  const showFeatured = searchParams.get('featured') === 'true';
  
  const [products, setProducts] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const { user } = useAuth();

  // Drag scroll handlers
  const handleMouseDown = (e) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  // Filtros de categoria baseados na página
  const getCategoryFilters = () => {
    if (category === 'feminino') {
      return [
        { name: 'Todos', filter: '' },
        { name: 'Fitness', filter: 'fitness' },
        { name: 'Vestidos', filter: 'vestidos' },
        { name: 'Calças', filter: 'calcas' },
        { name: 'Blusas', filter: 'blusas' },
        { name: 'Shorts', filter: 'shorts' },
        { name: 'Country', filter: 'country' },
      ];
    } else if (category === 'masculino') {
      return [
        { name: 'Todos', filter: '' },
        { name: 'Camisetas', filter: 'camisetas' },
        { name: 'Camisas', filter: 'camisas' },
        { name: 'Calças', filter: 'calcas' },
        { name: 'Bermudas', filter: 'bermudas' },
        { name: 'Country', filter: 'country' },
      ];
    } else if (category === 'infantil') {
      return [
        { name: 'Todos', filter: '' },
        { name: 'Meninos', filter: 'meninos' },
        { name: 'Meninas', filter: 'meninas' },
      ];
    }
    // Todos os produtos
    return [
      { name: 'Todos', filter: '' },
      { name: 'Masculino', filter: 'masculino', isCategory: true },
      { name: 'Feminino', filter: 'feminino', isCategory: true },
      { name: 'Infantil', filter: 'infantil', isCategory: true },
      { name: 'Fitness', filter: 'fitness' },
      { name: 'Vestidos', filter: 'vestidos' },
      { name: 'Calças', filter: 'calcas' },
      { name: 'Blusas', filter: 'blusas' },
      { name: 'Camisetas', filter: 'camisetas' },
      { name: 'Camisas', filter: 'camisas' },
      { name: 'Shorts', filter: 'shorts' },
    ];
  };

  const categoryFilters = getCategoryFilters();

  const handleFilterClick = (filter, isCategory = false) => {
    trackCategoryClick(category || 'todos', filter);
    if (isCategory) {
      // Redireciona para a página da categoria
      window.location.href = `/produtos/${filter}`;
      return;
    }
    const newParams = new URLSearchParams(searchParams);
    if (filter) {
      newParams.set('subcategory', filter);
    } else {
      newParams.delete('subcategory');
    }
    setSearchParams(newParams);
  };

  // Scroll para o topo quando a página carrega
  useEffect(() => {
    window.scrollTo(0, 0);
    // Reset scroll do filtro para mostrar "Todos" primeiro
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, []);

  const fetchFavorites = async () => {
    if (user) {
      try {
        const res = await axios.get(`${API}/favorites`);
        setFavoriteIds(res.data);
      } catch (e) {
        // Silent fail
      }
    } else {
      setFavoriteIds([]);
    }
  };

  useEffect(() => {
    fetchFavorites();
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    let retryTimer;

    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = {};
        if (category) params.category = category;
        if (showFeatured) params.featured = true;
        if (search) params.search = search;

        // Busca resiliente: re-tenta automaticamente em caso de cold start pós-deploy.
        const response = await apiGetWithRetry('/products', { params });
        if (cancelled) return;
        let data = response.data;

        // Filter for new products
        if (showNew) {
          data = data.filter(p => p.is_new);
        }

        // Filter by subcategory
        if (subcategoryFilter) {
          data = data.filter(p => 
            p.subcategory?.toLowerCase() === subcategoryFilter.toLowerCase()
          );
        }

        // Sort by sort_order
        data.sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));

        setProducts(data);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        console.error('Error fetching products (irá re-tentar):', e);
        // NUNCA mostra "Nenhum produto" por falha transitória: mantém o skeleton
        // e re-tenta em background até o backend responder.
        retryTimer = setTimeout(fetchProducts, 4000);
      }
    };

    fetchProducts();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [category, search, showNew, showFeatured, subcategoryFilter]);

  // Get page title
  const getPageTitle = () => {
    if (search) return `Resultados para "${search}"`;
    if (showNew) return 'Novidades';
    if (showFeatured) return 'Em Alta';
    
    // Se tem subcategoria selecionada e não tem categoria (página todos os produtos)
    if (subcategoryFilter && !category) {
      const selectedFilter = categoryFilters.find(f => f.filter === subcategoryFilter);
      return selectedFilter ? selectedFilter.name : 'Todos os Produtos';
    }
    
    return categoryTitles[category] || 'Todos os Produtos';
  };

  return (
    <div className="min-h-screen bg-white" data-testid="products-page">
      {/* Banner promocional: frete grátis 2+ peças com cronômetro 24h */}
      <FreeShippingPromoBanner />
      {/* Filtros de categoria */}
      <div className="py-4 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            ref={scrollRef}
            className={`flex justify-start gap-2 md:gap-3 overflow-x-auto pb-1 category-scroll ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            {categoryFilters.map((cat) => (
              <button
                key={cat.name}
                onClick={() => !isDragging && handleFilterClick(cat.filter, cat.isCategory)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-300 flex-shrink-0 ${
                  subcategoryFilter === cat.filter || (!subcategoryFilter && cat.filter === '')
                    ? 'bg-[#2d2d2d] border-[#2d2d2d] text-white'
                    : 'bg-[#f7f7f7] border-gray-200 text-gray-700 hover:border-[#2d2d2d] hover:text-gray-900'
                }`}
                data-testid={`filter-${cat.filter || 'todos'}`}
              >
                <picture>
                  <source srcSet="/logo-new-sm.webp" type="image/webp" />
                  <img 
                    src="/logo-new-sm.png" 
                    alt="" 
                    className="w-4 h-4 md:w-5 md:h-5 object-contain pointer-events-none"
                    loading="lazy"
                  />
                </picture>
                <span className="text-xs md:text-sm font-medium whitespace-nowrap pointer-events-none">
                  {cat.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Header: mostra título APENAS quando há subcategoria selecionada (esconde "Todos os Produtos") */}
      {subcategoryFilter && (
        <div className="bg-white py-4 md:py-5">
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-xl md:text-2xl font-semibold text-gray-900 uppercase tracking-wide">
              {getPageTitle()}
            </h1>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 pb-6 md:pb-8">
        {/* Products grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/5] bg-gray-200 mb-4" />
                <div className="h-4 bg-gray-200 mb-2" />
                <div className="h-4 bg-gray-200 w-2/3" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">Nenhum produto encontrado</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {[...products].sort((a, b) => {
              const aFav = favoriteIds.includes(a.product_id) ? 0 : 1;
              const bFav = favoriteIds.includes(b.product_id) ? 0 : 1;
              return aFav - bFav;
            }).map((product, index) => (
              <ProductCard 
                key={product.product_id} 
                product={product}
                isFavorited={favoriteIds.includes(product.product_id)}
                onFavoriteChange={fetchFavorites}
                priority={index < 4}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
